import React, { useEffect, useState } from 'react';
import config from '../../config';
import s from './BookingActivity.module.scss';

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const truncate = (value, max = 80) => {
  const text = String(value ?? '').trim();
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const rupees = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN')}` : String(value ?? '-');
};

const isTrue = (value) => String(value).toLowerCase() === 'true';

const yesNo = (value) => (isTrue(value) ? 'Yes' : 'No');

const FIELD_LABELS = {
  pickup_time: 'Pickup time',
  pickup_address: 'Pickup address',
  drop_address: 'Drop address',
  vehicle_class_id: 'Vehicle class',
  booking_type: 'Booking type',
  remarks: 'Remarks',
  delivery_deadline: 'Delivery deadline',
  distance: 'Distance',
  duration: 'Duration',
  toll_charge: 'Toll',
  payment_estimate: 'Estimate',
};

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (field === 'payment_estimate' || field === 'toll_charge') return rupees(value);
  if (field === 'distance') return `${value} km`;
  if (field === 'duration') return `${value} min`;
  if (field === 'vehicle_class_id') return `Class ${value}`;
  if (field === 'pickup_time' || field === 'delivery_deadline') return formatDateTime(String(value).replace(' ', 'T')) || String(value);
  return truncate(value, 60);
};

const changeLines = (act) => Object.entries(act.details?.changes || {}).map(
  ([field, c]) => `${FIELD_LABELS[field] || field}: ${formatFieldValue(field, c.old)} → ${formatFieldValue(field, c.new)}`,
);

const SOURCE_LABELS = {
  whatsapp: 'WhatsApp',
  ops: 'Ops',
  b2b: 'B2B portal',
  b2b_portal: 'B2B portal',
  manual: 'Manual entry',
};

const stateName = (value) => {
  const text = String(value ?? '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '-';
};

// Some writers store states in lowercase; a case-only change is not worth showing.
const isCaseOnlyStateChange = (act) => act.event_type === 'state_changed'
  && String(act.old_value ?? '').toLowerCase() === String(act.new_value ?? '').toLowerCase();

const EVENTS = {
  created: {
    icon: 'fa-plus', color: '#10b981',
    label: (a) => {
      const src = a.details?.source;
      const via = src ? ` via ${SOURCE_LABELS[String(src).toLowerCase()] || src}` : '';
      return `Booking created${via}${a.details?.from_draft_id ? ` from draft #${a.details.from_draft_id}` : ''}`;
    },
    lines: (a) => [
      a.details?.pickup_time ? `Pickup: ${formatDateTime(String(a.details.pickup_time).replace(' ', 'T')) || a.details.pickup_time}` : null,
      a.details?.payment_estimate != null ? `Estimate: ${rupees(a.details.payment_estimate)}` : null,
    ].filter(Boolean),
  },
  state_changed: {
    icon: 'fa-exchange', color: '#f59e0b',
    label: (a) => `Status changed from ${stateName(a.old_value)} to ${stateName(a.new_value)}${a.details?.reason ? ` (${truncate(a.details.reason)})` : ''}`,
  },
  amount_changed: {
    icon: 'fa-inr', color: '#3b82f6',
    label: (a) => `Amount changed from ${rupees(a.old_value)} to ${rupees(a.new_value)}`,
  },
  payment_plan_changed: {
    icon: 'fa-sliders', color: '#3b82f6',
    label: (a) => (a.new_value == null
      ? 'Collect-now amount cleared'
      : `Collect-now amount set to ${rupees(a.new_value)}${a.old_value != null ? ` (was ${rupees(a.old_value)})` : ''}`),
  },
  payment_received: {
    icon: 'fa-check', color: '#10b981',
    label: (a) => `Payment received: paid ${rupees(a.old_value)} to ${rupees(a.new_value)}${a.details?.fully_paid ? ' (fully paid)' : ''}`,
  },
  other_party_phone_changed: {
    icon: 'fa-phone', color: '#8b5cf6',
    label: (a) => `Other party phone changed${a.old_value ? ` from ${a.old_value}` : ''} to ${a.new_value || '-'}`,
  },
  other_party_pays_changed: {
    icon: 'fa-user', color: '#8b5cf6',
    label: (a) => `Other party pays: ${yesNo(a.old_value)} to ${yesNo(a.new_value)}`,
  },
  on_hold_changed: {
    icon: 'fa-pause', color: '#ef4444',
    label: (a) => (isTrue(a.new_value) ? 'Booking put on hold' : 'Booking taken off hold'),
  },
  material_changed: {
    icon: 'fa-cube', color: '#6b7280',
    label: (a) => `Material changed${a.old_value ? ` from "${truncate(a.old_value, 40)}"` : ''} to "${truncate(a.new_value, 40) || '-'}"`,
  },
  remarks_changed: {
    icon: 'fa-comment', color: '#ec4899',
    label: (a) => (a.new_value ? `Remarks updated: "${truncate(a.new_value)}"` : 'Remarks cleared'),
  },
  booking_type_changed: {
    icon: 'fa-cubes', color: '#06b6d4',
    label: (a) => `Converted from ${a.old_value || 'standard'} to part load${a.details?.box_count ? ` (${a.details.box_count} box${Number(a.details.box_count) === 1 ? '' : 'es'})` : ''}`,
  },
  booking_modified: {
    icon: 'fa-pencil', color: '#6b7280',
    label: () => 'Booking details modified',
    lines: changeLines,
  },
};

/**
 * Change history for a booking. Refetches whenever `refreshKey` changes so edits made from the
 * same panel show up straight away.
 */
const BookingActivity = ({ bookingId, refreshKey }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!bookingId) return undefined;
    let cancelled = false;
    setError(false);
    fetch(`${config.customerApiBase}/bookings/${bookingId}/activities`)
      .then((res) => {
        if (!res.ok) throw new Error(`API ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setActivities(Array.isArray(data.activities) ? data.activities : []);
      })
      .catch((err) => {
        console.error('Error loading booking activity:', err);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [bookingId, refreshKey]);

  const visible = activities.filter((act) => !isCaseOnlyStateChange(act));

  return (
    <div className={s.section} onClick={(e) => e.stopPropagation()}>
      <div className={s.header}>
        <i className="fa fa-history" aria-hidden="true" /> Activity
      </div>
      {loading && <div className={s.hint}>Loading...</div>}
      {!loading && error && <div className={s.hint}>Could not load activity.</div>}
      {!loading && !error && visible.length === 0 && <div className={s.hint}>No activity yet.</div>}
      <div className={s.list}>
        {visible.map((act) => {
          const meta = EVENTS[act.event_type];
          return (
            <div key={act.id} className={s.item}>
              <div className={s.icon} style={{ background: meta?.color || '#9ca3af' }}>
                <i className={`fa ${meta?.icon || 'fa-circle'}`} aria-hidden="true" />
              </div>
              <div className={s.body}>
                <div className={s.text}>{meta ? meta.label(act) : act.event_type}</div>
                {meta?.lines ? meta.lines(act).map((line) => (
                  <div key={line} className={s.detail}>{line}</div>
                )) : null}
                <div className={s.time}>
                  {formatDateTime(act.created_at)}{act.created_by ? ` · ${act.created_by}` : ''}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BookingActivity;
